/* eslint-disable react-hooks/exhaustive-deps */
import { useExportDownload } from '../../../utils/useExportDownload';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { DateFilter, Icon, Loading, OnlineStatusCellRenderer, Text } from '@components';
import { V2FilterPayload } from '../../AssetFilter/functions/V2FilterPayload';
import {
  createGridFetcher,
  useAnalyticsEffects,
  handleMapChange,
  handleSubmitReport,
  checkIfErrorExist,
  onChangeStartDate,
  onChangeEndDate,
} from './Utils';
import { Api } from '../../../services';
import Swal from 'sweetalert2';
import { AgGridReact } from 'ag-grid-react';
import { Card, Col, Row } from 'react-bootstrap';
import { PagedModel } from '../../../models/PagedModel';
import { isAdmin, isAzureEnv, isSystemAdmin } from '../../../utils';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react/lib/agGridReact';
import { DefaultGridHeader } from './Components/DefaultGridHeader';
import { AnalyticsGridHeader } from './Components/AnalyticsGridHeader';
import {
  CellClickedEvent,
  ColDef,
  ColGroupDef,
  GridReadyEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import styled from 'styled-components';

const RowCounter = styled.input`
  min-width: 4rem;
  width: ${(props) => props.value.toString().length + 0.5 + 'rem'} !important;
  text-align: center;
`;

export interface IPagedTableProps<T> {
  id: string;
  initQueryParams: PagedQueryParams;
  assetFilters?: string[];
  columnDefs: (ColDef | ColGroupDef)[];
  gridProps?: any;
  isLoading: boolean;
  ignoreFetch?: boolean;
  disabledFilters: string[];
  rowData?: PagedModel<T>;
  csvData?: any;
  csvFileName?: string;
  csvHeaders?: string | string[];
  reload?: boolean;
  handleFetchData?: (filter?: any, isCSV?: boolean, queryState?: any, pageType?: string) => void;
  triggerUpdate?: boolean;
  onCellClick?(e: T, field: string): void;
  enumTranslation?: any;
  searchControl?: boolean;
  dateControl?: boolean;
  filterControl?: boolean;
  viewingControl?: boolean;
  pagerControl?: boolean;
  exportControl?: boolean;
  tabKey?: string[];
  filterReportType?: string[];
  reportType?: string;
  reportLabel?: string;
  ChangeReportType?: () => void;
  anchorEl?: string;
  setAnchorEl?: string;
  deleteRow?: any;
  onMapChange?: any;
}

export class PagedQueryParams {
  constructor(
    id?: string,
    defaultSortField: string = 'name',
    defaultSortOrder: 'asc' | 'desc' = 'asc',
  ) {
    let searchHistory = JSON.parse(sessionStorage.getItem('searchHistory'));
    let searchNotEmpty = Boolean(searchHistory) && Object.keys(searchHistory).length !== 0;

    this.filter = searchNotEmpty && Boolean(searchHistory[id]) ? searchHistory[id] : '';
    this.sort = { colId: defaultSortField, sort: defaultSortOrder };
    this.pageNumber = 1;
    this.pageSize = 20;
    this.dateFilters = {
      start: undefined,
      end: undefined,
    };
  }

  public filter?: string;
  public sort?: { colId: string; sort: string };
  public pageNumber?: number;
  public pageSize?: number;
  public dateFilters?: { start: any; end: any };
  public mapId?: any;

  orderBy = function (): string {
    return this.sort.colId.trim() + ' ' + this.sort.sort.trim();
  };

  skip = function (): number {
    if (this.pageNumber === 0) {
      return 0;
    } else return this.pageSize * (this.pageNumber - 1);
  };

  setSort = function (): any[] {
    let sorts = [
      {
        field: this.sort.colId,
        ascending: this.sort.sort === 'asc',
      },
    ];
    return sorts;
  };

  setInitialSort(field: string, direction: string): PagedQueryParams {
    this.sort = { colId: field, sort: direction };
    return this;
  }
}

function GridBody<T>(
  props: IPagedTableProps<T> & {
    agGridRef: React.MutableRefObject<AgGridReactType | undefined>;
    components: any;
    onSortChanged: (e: SortChangedEvent) => void;
    onGridReady: (e: GridReadyEvent) => void;
    onCellClick: (e: CellClickedEvent) => void;
    defaultColDef: ColDef;
  },
) {
  return (
    <GridContext.Provider value={props.rowData}>
      <div className="d-flex flex-column h-100 w-100 pb-5">
        <div className="ag-theme-material h-100 w-100 position-relative">
          <AgGridReact
            ref={props.agGridRef}
            rowData={props.rowData?.items || props.rowData}
            columnDefs={props.columnDefs}
            components={props.components}
            onSortChanged={props.onSortChanged}
            onGridReady={props.onGridReady}
            onCellClicked={props.onCellClick}
            animateRows={false}
            defaultColDef={props.defaultColDef}
            enableSorting={true}
            suppressNoRowsOverlay={true}
            suppressDragLeaveHidesColumns
            tooltipShowDelay={250}
            {...props.gridProps}
          />
        </div>
      </div>
    </GridContext.Provider>
  );
}

export const GridContext = createContext({});
export const PageTypeContext = createContext(null);

export function ServerSideGrid<T>(props: IPagedTableProps<T>) {
  //useState
  const [andOr, setAndOr] = useState<string>('or');
  const [queryState, setQueryState] = useState<PagedQueryParams>(props.initQueryParams);
  const [csvReady, setCsvReady] = useState(false);
  const [submitShowContent, setSubmitShowContent] = useState(false);
  const [selectedMap, setSelectedMap] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [disableBtn, setDisableBtn] = useState(true);
  const [dateKey, setDateKey] = useState(0);
  const [filterKey, setFilterKey] = useState(0);
  const [dateError, setDateError] = useState(false);
  const [assetFilters, setAssetFilters] = useState<string[]>(
    props.disabledFilters
      ? (props.assetFilters || []).filter(
          (item) => !props.disabledFilters.some((value) => item && item.includes(value)),
        )
      : props.assetFilters || [],
  );

  // Grabbing Analytics UseEffects
  useAnalyticsEffects({
    reportType: props.reportType,
    initQueryParams: props.initQueryParams,
    setQueryState,
    queryState,
    setAndOr,
    setSubmitShowContent,
    setDateKey,
    setDisableBtn,
    selectedMap,
    checkIfErrorExist,
    isAnalyticsPage: props.id === 'analytic-reports',
  });

  //useRefs Hooks
  const agGridRef = useRef<AgGridReactType>();
  const csvLinkRef = useRef(null);
  const gridApiRef = useRef<any>(null);

  //Consts
  const open = Boolean(anchorEl);
  const pageType = props.tabKey || props.id;
  const authorized = isAdmin() || isSystemAdmin();
  const csvHeaders = props.csvHeaders || [];
  const shouldExport = props.id !== 'Assets';
  const { debounceFetchData } = useMemo(() => createGridFetcher<T>(agGridRef), []);

  useExportDownload(shouldExport && csvReady ? props.csvData : null, props.csvFileName);

  const components = useMemo(() => {
    return {
      onlineStatusRenderer: OnlineStatusCellRenderer,
      agLoadingOverlay: Loading,
    };
  }, []);

  /*************************************************************
   * useEffect Section
   *************************************************************/

  // Resets assetFilters/Searching When Switching Tabs (e.g., Places Details, Device Mgmt)
  useEffect(() => {
    setFilterKey((prevKey) => prevKey + 1);
    if (queryState?.filter) {
      queryState.filter = '';
    }
  }, [props.id]);

  // Resets to page 1 whenever the user switches tabs
  useEffect(() => {
    setQueryState({ ...queryState, pageNumber: 1 } as PagedQueryParams);
  }, [props.tabKey]);

  // Get New Data on Parameter Update for Normal Grid
  useEffect(() => {
    //prevents refetch when pager is empty input when backspacing
    const isPageNumberValid = Number.isInteger(queryState.pageNumber) && queryState.pageNumber > 0;
    if (!props.ignoreFetch && isPageNumberValid) {
      debounceFetchData(queryState, assetFilters, andOr, props, false);
    }
  }, [props.triggerUpdate, andOr, queryState, assetFilters, props.reload]);

  // Get New Data on Parameter Update for Analytic Reports Grid
  useEffect(() => {
    if (dateError) return;
    if (dateError) return;
    //prevents refetch when pager is empty input when backspacing
    const isPageNumberValid = Number.isInteger(queryState.pageNumber) && queryState.pageNumber > 0;
    if (props.ignoreFetch && props.reportType && submitShowContent && isPageNumberValid) {
      debounceFetchData(queryState, assetFilters, andOr, props, false);
    }
  }, [queryState?.sort, queryState?.filter, queryState?.pageNumber, queryState?.pageSize]);

  // Auto-click CSV link for asset export
  useEffect(() => {
    if (props.id === 'Assets' && csvReady && csvLinkRef) {
      setTimeout(() => csvLinkRef.current.link.click(), 0);
    }
  }, [props.csvData]);

  // Remove deleted row from grid
  useEffect(() => {
    if (props.deleteRow) {
      const { api } = agGridRef.current;
      const { rowData } = agGridRef.current.props;
      let Data = rowData.filter((obj, i) => {
        return obj.id !== props.deleteRow.id;
      });
      Data.length !== undefined && Data.length !== 0 && api.setRowData(Data);
    }
  }, [props.deleteRow]);

  useEffect(() => {
    if (props.isLoading) {
      agGridRef.current?.api?.showLoadingOverlay();
    } else {
      agGridRef.current?.api?.hideOverlay();
    }
  }, [props.isLoading]);

  /*************************************************************
   *  DEFAULT GRID HEADER FUNCTIONS
   *************************************************************/

  const fetchCSV = () => {
    setCsvReady(true);
    if (props.id === 'Assets') {
      const payload = V2FilterPayload(
        assetFilters,
        andOr,
        0,
        queryState.setSort(),
        props.rowData?.count ?? queryState.pageSize, // export all rows
        undefined,
        queryState.filter,
        queryState.dateFilters.start,
        queryState.dateFilters.end,
        queryState.mapId,
      );

      Api.post(`/v2/assets/filter/background-export`, payload)
        .then((res) => {
          if (res.status === 202) {
            Swal.fire({
              icon: 'success',
              title: 'Export Request Submitted',
              html: `
              <div>Sonar is processing the requested export.</div>
              <div>You will receive an email with a link</div>
              <div>to download the completed file when it is ready.</div>
              <div>You can now safely navigate away from this page.</div>
            `,
              confirmButtonText: 'Okay',
              customClass: {
                htmlContainer: 'swal2HtmlContainer',
              },
            });
          } else {
            throw new Error('Export request failed');
          }
        })
        .catch((err) => {
          console.error(err);
          Swal.fire('Error', 'Failed to submit export request.', 'error');
        });
    } else {
      debounceFetchData(queryState, assetFilters, andOr, props, true);
    }
  };

  const handleFilterChange = (e: Array<string>, type: string) => {
    if (JSON.stringify(e) !== JSON.stringify(assetFilters)) {
      setAssetFilters(e);
    }
    if (type !== andOr) {
      setAndOr(type);
    }
    queryState.pageNumber = 1;
  };

  const onChangeLimit = (e: React.ChangeEvent<HTMLInputElement>) => {
    const requestedPageSize = parseInt(e.target.value, 10);

    // If user clears the input, let it be handled elsewhere (or just ignore here)
    if (Number.isNaN(requestedPageSize)) return;

    const maxCount = props.rowData?.count ?? 1;

    const hardCap = props.id === 'MapList' ? 100 : Infinity;
    const maxAllowed = Math.min(maxCount, hardCap);

    const pageSize = Math.min(Math.max(1, requestedPageSize), maxAllowed);

    const totalPages = Math.ceil((props.rowData?.count || 0) / pageSize);
    const currentPage = Math.min(queryState.pageNumber, totalPages || 1);

    setQueryState({
      ...queryState,
      pageSize,
      pageNumber: currentPage,
    } as PagedQueryParams);
  };

  // Compares items to filter size so that filter size is never greater than number of items.
  function adjustPageSize() {
    //queryState.pageSize is set on page load and sets the Viewing number of assets
    return queryState.pageSize > props.rowData?.count
      ? props.rowData?.count + ''
      : queryState.pageSize + '';
  }

  // Prevents users from copy/pasting a negative number into inputs
  const preventPasteNegative = (e) => {
    const clipboardData = e.clipboardData;
    const pastedData = parseFloat(clipboardData.getData('text'));
    if (pastedData < 0) {
      e.preventDefault();
    }
  };

  /*************************************************************
   * GRID BODY FUNCTIONS
   *************************************************************/

  function getDefaultColDef(): ColDef {
    return {
      sortable: true,
      sortingOrder: ['asc', 'desc', null],
      filter: false,
      comparator: function (valueA, valueB, nodeA, nodeB, isInverted) {
        return 0;
      },
      flex: 1,
      resizable: true,
      singleClickEdit: true,
      filterParams: {
        resetButton: true,
      },
    } as ColDef;
  }

  //Sizes grid columns to fit and shows loading overlay if needed
  function onGridReady(e: GridReadyEvent) {
    gridApiRef.current = e.api;
  }

  function onCellClick(e: CellClickedEvent) {
    if (props.onCellClick) {
      props.onCellClick(e.data as T, e.colDef.field);
    }
  }

  /*************************************************************
   * RENDERING SSG COMPONENTS
   *************************************************************/

  // Render only for Analytics
  let dateFilterRender = (queryState, dateKey, reportType) => {
    return (
      <DateFilter
        key={dateKey}
        startDate={queryState.dateFilters.start}
        endDate={queryState.dateFilters.end}
        onChangeStartDate={(e) => onChangeStartDate(e, queryState, setQueryState)}
        onChangeEndDate={(e) => onChangeEndDate(e, queryState, setQueryState)}
        reportType={reportType}
        onDateErrorChange={setDateError}
      />
    );
  };

  // Render only for Analytics
  function renderAnalyticsHeader() {
    if (props.id === 'analytic-reports') {
      return (
        <AnalyticsGridHeader
          reportType={props.reportType}
          filterControl={props.filterControl}
          onMapChange={(mapId, mapName) =>
            handleMapChange(
              mapId,
              mapName,
              props,
              setSelectedMap,
              setQueryState,
              checkIfErrorExist,
              queryState,
              setDisableBtn,
            )
          }
          ChangeReportType={props.ChangeReportType}
          onFilterChange={handleFilterChange}
          onSubmit={() =>
            handleSubmitReport(
              queryState,
              setQueryState,
              debounceFetchData,
              assetFilters,
              andOr,
              props,
              setSubmitShowContent,
            )
          }
          showBell={props.reportType === 'battery' && authorized && !isAzureEnv()}
          authorized={authorized}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          open={open}
          disableBtn={disableBtn}
          renderDateFilters={dateFilterRender(queryState, dateKey, props.reportType)}
        />
      );
    }
    return null;
  }

  // Render Default Grid Header for all Pages || Render Default Grid Header for Analytics when Submit
  function renderDefaultHeader() {
    const shouldShow = props.id !== 'analytic-reports' || submitShowContent;
    const totalCount = props.rowData?.count ?? 0;
    const maxViewing = props.id === 'MapList' ? Math.min(100, totalCount) : totalCount;

    if (!shouldShow) return null;
    return (
      <DefaultGridHeader
        id={props.id}
        disabledFilters={props.disabledFilters}
        queryState={queryState}
        setQueryState={setQueryState}
        rowData={props.rowData}
        filterKey={filterKey}
        handleFilterChange={handleFilterChange}
        adjustPageSize={adjustPageSize}
        onChangeLimit={onChangeLimit}
        maxViewing={maxViewing}
        preventPasteNegative={preventPasteNegative}
        searchControl={props.searchControl}
        filterControl={props.id === 'analytic-reports' ? false : props.filterControl}
        viewingControl={props.viewingControl}
        pagerControl={props.pagerControl}
        exportControl={props.exportControl}
        pageType={pageType}
        csvData={props.csvData}
        csvFileName={props.csvFileName}
        csvHeaders={csvHeaders}
        csvLinkRef={csvLinkRef}
        fetchCSV={fetchCSV}
        isLoading={props.isLoading}
        hasDateError={dateError}
      />
    );
  }

  //  Render GridBody || Render Analytics MSG until Submit then Render GridBody
  function renderGridContent() {
    if (props.id === 'analytic-reports' && !submitShowContent) {
      return (
        <div className="report-msg">
          <Icon
            name="analytics"
            size="6x"
            className="mb-3"
            variant="dark"
            disabled={false}
            showPointer={false}
          />
          <Text className="mb-3" as="h5">
            To Generate Reports:
          </Text>
          <Text className="mb-4" as="h5">
            Select a Report, Adjust Filters & Click Submit.
          </Text>
        </div>
      );
    }

    return (
      <GridBody
        {...props}
        agGridRef={agGridRef}
        components={components}
        onSortChanged={(e: SortChangedEvent) => {
          const columnApi = agGridRef.current?.columnApi;
          const colState = columnApi?.getColumnState() ?? [];
          const active = colState.find((c) => c.sort != null);

          // Third click: no active sorts in the grid
          if (!active) {
            // 1) Ask the server for default sorting again (Name ASC)
            setQueryState((prev) => {
              const updated = new PagedQueryParams();
              Object.assign(updated, prev);
              updated.sort = { colId: 'name', sort: 'asc' };
              return updated;
            });

            // 2) Clear all sort arrows (Patrick wants no arrow showing)
            columnApi?.applyColumnState({
              defaultState: { sort: null, sortIndex: null },
              state: [],
            });

            return;
          }

          // Normal ASC/DESC clicks: sync current column + direction to queryState
          setQueryState((prev) => {
            const updated = new PagedQueryParams();
            Object.assign(updated, prev);
            updated.sort = { colId: active.colId, sort: active.sort as 'asc' | 'desc' };
            return updated;
          });
        }}
        onGridReady={onGridReady}
        onCellClick={(e) => onCellClick(e)}
        defaultColDef={getDefaultColDef()}
      />
    );
  }

  return (
    <>
      {/* Renders Head & Grid for Places Details */}
      {props.id.includes('Grid') ? (
        <div className="h-100">
          {renderDefaultHeader()}
          {renderGridContent()}
        </div>
      ) : (
        <>
          {/* Renders for All SSG Pages */}
          {renderAnalyticsHeader()}
          <Card className="h-100 overflow-hidden">
            <Card.Body>
              <Row className="h-100">
                <Col className="h-100">
                  {renderDefaultHeader()}
                  {renderGridContent()}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </>
      )}
    </>
  );
}
